import {Article, CollectionTrackingContext, Product, WishlistCollection} from '../../@types/wishlist.js';

export type ProductActionMenuVariant = 'default' | 'purchased';

export interface ProductMenuState extends UpdatableContext<ProductMenuStateData> {
  openActionMenu: (
    product: Product,
    collection: WishlistCollection | null,
    imageUrl: string,
    selectedArticle: Article | null,
    variant?: ProductActionMenuVariant
  ) => void;
  closeActionMenu: () => void;
  openRemoveArticleMenu: (product: Product, collection?: WishlistCollection, backButtonCallback?: (() => void)) => void;
  closeRemoveArticleMenu: () => void;
  setLoginState: (loggedIn: boolean) => void;
}

export interface ProductMenuStateData {
  product: Product | null;
  loggedIn: boolean;
  selectedArticle: Article | null;
  collection: WishlistCollection | null;
  imageUrl: string;
  actionMenuOpen: boolean;
  removeArticleMenuOpen: boolean;
  actionMenuVariant: ProductActionMenuVariant;
  backButtonCallback?: (() => void);
}

export interface CollectionMenuStateData {
  collection: WishlistCollection | null;
  openActionMenu: boolean;
  productToAdd: Product | null;
  openUpsertLayer: boolean;
  openShareView: boolean;
  openRemoveCollectionView: boolean;
  openRemoveArticleView: boolean;
  collectionTrackingContext: CollectionTrackingContext | null;
  backButtonCallback?: (() => void);
}

export interface CollectionMenuState extends UpdatableContext<CollectionMenuStateData> {
  openRenameLayer: (wishlistCollection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext, backButtonCallback?: (() => void)) => void;
  openCreateLayer: (productToAdd?: Product | null, backButtonCallback?: (() => void)) => void;
  openActionMenu: (wishlistCollection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext) => void;
  openShareView: (wishlistCollection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext, backButtonCallback?: (() => void)) => void;
  openRemoveCollectionView: (wishlistCollection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext, backButtonCallback?: (() => void)) => void;
  closeUpsertLayer: () => void;
  closeActionMenu: () => void;
  closeShareView: () => void;
  closeRemoveCollectionView: () => void;
}

export class ProductMenuStateImpl implements ProductMenuState {
  context = EMPTY_PRODUCT_MENU_STATE_DATA;
  openActionMenu = (
    product: Product,
    collection: WishlistCollection | null,
    imageUrl: string,
    selectedArticle: Article | null,
    variant: ProductActionMenuVariant = 'default'
  ): void => {
    this.update({
      product,
      collection,
      imageUrl,
      selectedArticle,
      actionMenuOpen: true,
      actionMenuVariant: variant,
    });
  };
  closeActionMenu = (): void => {
    this.update({
      actionMenuOpen: false,
    });
  };
  openRemoveArticleMenu = (product: Product, collection?: WishlistCollection, backButtonCallback?: (() => void)): void => {
    this.update({
      product,
      collection,
      removeArticleMenuOpen: true,
      backButtonCallback
    });
  };
  closeRemoveArticleMenu = (): void => {
    this.update({
      removeArticleMenuOpen: false,
    });
  };
  setLoginState = (loggedIn: boolean): void => {
    this.update({
      loggedIn,
    });
  };
  update = (newState: Partial<ProductMenuStateData>): void => {
    throw new Error(`update not implemented, input[${JSON.stringify(newState)}]`);
  };
}

export class CollectionMenuStateImpl implements CollectionMenuState {
  context = EMPTY_COLLECTION_MENU_STATE_DATA;
  update = (newState: Partial<CollectionMenuStateData>): void => {
    throw new Error(`update not implemented, input[${JSON.stringify(newState)}]`);
  };
  openShareView = (
    wishlistCollection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext,
    backButtonCallback?: (() => void)
  ): void => {
    this.update({
      collection: wishlistCollection,
      openShareView: true,
      collectionTrackingContext,
      backButtonCallback,
      openActionMenu: false,
      openUpsertLayer: false,
    });
  };
  closeShareView = (): void => {
    this.update({
      openShareView: false,
      backButtonCallback: undefined,
    });
  };
  openRenameLayer = (
    wishlistCollection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext,
    backButtonCallback?: (() => void)
  ): void => {
    this.update({
      collection: wishlistCollection,
      productToAdd: null,
      openUpsertLayer: true,
      collectionTrackingContext,
      backButtonCallback,
      openActionMenu: false,
    });
  };
  openCreateLayer = (
    productToAdd: Product | null = null,
    backButtonCallback?: (() => void)
  ): void => {
    this.update({
      collection: null,
      productToAdd: productToAdd,
      openUpsertLayer: true,
      collectionTrackingContext: null,
      backButtonCallback,
      openActionMenu: false,
    });
  };

  closeUpsertLayer = (): void => {
    this.update({
      productToAdd: null,
      openUpsertLayer: false,
      backButtonCallback: undefined,
    });
  };
  openActionMenu = (
    wishlistCollection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext
  ): void => {
    this.update({
      collection: wishlistCollection,
      openActionMenu: true,
      collectionTrackingContext,
      backButtonCallback: undefined,
    });
  };
  closeActionMenu = (): void => {
    this.update({
      openActionMenu: false,
    });
  };
  openRemoveCollectionView = (
    wishlistCollection: WishlistCollection,
    collectionTrackingContext: CollectionTrackingContext,
    backButtonCallback?: (() => void)
  ): void => {
    this.update({
      collection: wishlistCollection,
      openRemoveCollectionView: true,
      collectionTrackingContext: collectionTrackingContext,
      backButtonCallback,
      openActionMenu: false,
    });
  };
  closeRemoveCollectionView = (): void => {
    this.update({
      openRemoveCollectionView: false,
      backButtonCallback: undefined,
    });
  };
}

export const EMPTY_PRODUCT_MENU_STATE_DATA: ProductMenuStateData = {
  product: null,
  loggedIn: false,
  selectedArticle: null,
  collection: null,
  imageUrl: '',
  actionMenuOpen: false,
  removeArticleMenuOpen: false,
  actionMenuVariant: 'default',
  backButtonCallback: undefined
};

export const EMPTY_COLLECTION_MENU_STATE_DATA: CollectionMenuStateData = {
  collection: null,
  openActionMenu: false,
  productToAdd: null,
  openUpsertLayer: false,
  openShareView: false,
  openRemoveCollectionView: false,
  openRemoveArticleView: false,
  collectionTrackingContext: null,
  backButtonCallback: undefined,
};

interface UpdatableContext<TYPE> {
  context: TYPE
  update: (newState: Partial<TYPE>) => void
}
